/**
 * The Models tab: the AI model catalogue (TMMPDM-0104, TMMAPI-0171 section A, MM-D-145). One row per
 * model, active first, then retired models only while "Show retired" is on (URL `retired=1`). Each
 * active model's prices sit beneath it behind a disclosure, in `ModelPricesTable` unchanged; a price
 * is added from the model's row, so the model is never typed. Prices whose model isn't an active
 * catalogue model (the staging typo) are a read-only group under the table.
 *
 * Composition rule: `usedBy`, `fast` / `deep` and every refusal's details are the server's, shown as
 * sent. Retired is `retiredAt` alone. The outside-the-catalogue group compares one price field with
 * the catalogue's ids for layout only; it is not a verdict. The page owns the two reads.
 * TMMPDM-0105 (TMMAPI-0172 F.17): an Efforts column (`efforts` as sent, or "Not checked"), and the
 * lifecycle chip under the id, from `lifecycle` alone. The outside group splits its rows by a
 * `modelId` lookup against the retired catalogue rows (layout): retired models' prices sit under
 * their own subhead, ids not in the catalogue at all under the 0104 caption.
 * TMMPDM-0106: `efforts: []` (Anthropic reports no effort level) reads "None (can't be assigned)"
 * and puts a "No effort support" chip beside any lifecycle chip, from `efforts` alone. "In use by"
 * is unchanged: a model that's both shows both, never a third verdict made from the two.
 * TMMPDM-0108 (TMMAPI-0174): a Structured output column (`structuredOutputs` as sent: Yes, "No (can't
 * be assigned)", or "Not checked") and a "No structured output" chip from `structuredOutputs === false`
 * alone, beside the other chips.
 */
import { Fragment, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { adminApi } from '@/api/tmm/admin';
import type { AiModelCatalogRow, AiModelPriceRow } from '@/api/tmm/types';
import { EmptyState, RowActionsMenu, useNotify } from '@/components/page';
import { AppSwitch } from '@/components/ui';
import { NOT_SENT, formatDate, formatInstant } from '@/format';
import { useCopy } from '@/hooks/useCopy';
import { StatusChip } from '@/status';
import { AddPriceDialog, type PriceChangeTarget } from './AddPriceDialog';
import { EditTiersDialog, RefusalAlert, RetireModelDialog } from './ModelCatalogDialogs';
import { LifecycleChip } from './LifecycleChip';
import { effortLabel, modelName, verifyFailure, type CatalogRefusal } from './modelCatalogCopy';
import { ModelPricesTable } from './ModelPricesTable';

const COLUMNS = 8;

function isRetired(row: AiModelCatalogRow): boolean {
  return typeof row.retiredAt === 'string' && row.retiredAt !== '';
}

/** Anthropic reports no effort level for the model: `efforts` is sent and empty (TMMPDM-0106). */
function takesNoEffort(row: AiModelCatalogRow): boolean {
  return row.efforts !== undefined && row.efforts.length === 0;
}

/** The "No effort support" chip; its reason is the tooltip and the accessible description. */
function NoEffortChip() {
  const { t } = useCopy();
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      title={t(
        'ai.catalog.noEffortReason',
        "Anthropic reports no effort support; this model can't be assigned.",
      )}
      describeChild
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    >
      <StatusChip
        tabIndex={0}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tone="warning"
        label={t('ai.catalog.noEffortChip', 'No effort support')}
      />
    </Tooltip>
  );
}

/** The "No structured output" chip; its reason is the tooltip and the accessible description. */
function NoStructuredChip() {
  const { t } = useCopy();
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      title={t(
        'ai.catalog.noStructuredReason',
        "Anthropic says this model doesn't take structured outputs; it can't be assigned.",
      )}
      describeChild
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    >
      <StatusChip
        tabIndex={0}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tone="warning"
        label={t('ai.catalog.noStructuredChip', 'No structured output')}
      />
    </Tooltip>
  );
}

interface ModelCatalogProps {
  models: AiModelCatalogRow[];
  prices: AiModelPriceRow[];
  locale: string;
  showRetired: boolean;
  onShowRetiredChange: (show: boolean) => void;
  /** The model whose price history is open (the page's URL `history`). */
  historyModel: string | undefined;
  onToggleHistory: (model: string) => void;
  /** Re-read the catalogue and the prices. */
  onReload: () => void;
}

export function ModelCatalog({
  models,
  prices,
  locale,
  showRetired,
  onShowRetiredChange,
  historyModel,
  onToggleHistory,
  onReload,
}: ModelCatalogProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(historyModel ? [historyModel] : []),
  );
  const [outsideOpen, setOutsideOpen] = useState(false);
  const [priceFor, setPriceFor] = useState<string | null>(null);
  const [change, setChange] = useState<PriceChangeTarget | null>(null);
  const [editing, setEditing] = useState<AiModelCatalogRow | null>(null);
  const [retiring, setRetiring] = useState<AiModelCatalogRow | null>(null);
  const [verifying, setVerifying] = useState<number | null>(null);
  const [verifyRefusal, setVerifyRefusal] = useState<CatalogRefusal | null>(null);

  /** A re-read after another write: a Verify refusal from before no longer applies. */
  const reread = () => {
    setVerifyRefusal(null);
    onReload();
  };

  const active = models.filter((row) => !isRetired(row));
  const retired = models.filter(isRetired);
  const activeIds = new Set(active.map((row) => row.modelId));
  const outside = prices.filter((row) => !activeIds.has(row.model));
  const retiredIds = new Set(retired.map((row) => row.modelId));
  const outsideRetired = outside.filter((row) => retiredIds.has(row.model));
  const outsideUnknown = outside.filter((row) => !retiredIds.has(row.model));

  const toggle = (modelId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });

  const verify = async (row: AiModelCatalogRow) => {
    if (verifying !== null) return;
    setVerifying(row.id);
    setVerifyRefusal(null);
    try {
      await adminApi.verifyAiModel(row.id);
      notify.success(t('ai.catalog.verified', 'Checked with Anthropic.'));
      onReload();
    } catch (err) {
      const failure = verifyFailure(t, err);
      setVerifyRefusal(failure.refusal);
      if (failure.notFound) onReload();
    } finally {
      setVerifying(null);
    }
  };

  const priceTable = (modelId: string, rows: AiModelPriceRow[]) => (
    <ModelPricesTable
      rows={rows}
      loading={false}
      locale={locale}
      showModel={false}
      ariaLabel={t('ai.catalog.pricesFor', 'Prices for {{model}}', { model: modelId })}
      emptyTitle={t('ai.catalog.noPrices', 'No prices yet. Add one before assigning this model.')}
      historyModel={historyModel}
      onToggleHistory={onToggleHistory}
      onChangePrice={(row) => setChange(row)}
      onReload={reread}
    />
  );

  const activeRow = (row: AiModelCatalogRow) => {
    const open = expanded.has(row.modelId);
    const name = modelName(row);
    const hasDisplayName = name !== row.modelId;
    const disclosureId = `model-prices-${row.id}`;
    return (
      <Fragment key={row.id}>
        <TableRow>
          <TableCell>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
              <IconButton
                size="small"
                aria-expanded={open}
                aria-controls={open ? disclosureId : undefined}
                aria-label={
                  open
                    ? t('ai.catalog.hidePrices', 'Hide prices for {{model}}', {
                        model: row.modelId,
                      })
                    : t('ai.catalog.showPrices', 'Show prices for {{model}}', {
                        model: row.modelId,
                      })
                }
                onClick={() => toggle(row.modelId)}
              >
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
              <Box>
                {hasDisplayName ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {name}
                  </Typography>
                ) : null}
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    ...theme.typography.mono,
                    ...(hasDisplayName
                      ? { color: theme.palette.text.secondary }
                      : { fontWeight: 600 }),
                  })}
                >
                  {row.modelId}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  <LifecycleChip row={row} locale={locale} />
                  {takesNoEffort(row) ? <NoEffortChip /> : null}
                  {row.structuredOutputs === false ? <NoStructuredChip /> : null}
                </Stack>
              </Box>
            </Stack>
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5}>
              {row.fast ? (
                <StatusChip tone="neutral" label={t('ai.catalog.tier.FAST', 'Fast')} />
              ) : null}
              {row.deep ? (
                <StatusChip tone="neutral" label={t('ai.catalog.tier.DEEP', 'Deep')} />
              ) : null}
            </Stack>
          </TableCell>
          <TableCell>
            {/* The model's `efforts` as sent; absent until a Verify since TMMAPI-0172. */}
            {takesNoEffort(row) ? (
              t('ai.catalog.effortsNone', "None (can't be assigned)")
            ) : row.efforts !== undefined ? (
              row.efforts.map((effort) => effortLabel(t, effort)).join(', ')
            ) : (
              <>
                {t('ai.catalog.effortsNotChecked', 'Not checked')}
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('ai.catalog.effortsHint', 'Verify fills this in.')}
                </Typography>
              </>
            )}
          </TableCell>
          <TableCell>
            {/* The model's `structuredOutputs` as sent; absent until a Verify since TMMAPI-0174. */}
            {row.structuredOutputs === true ? (
              t('ai.catalog.structuredYes', 'Yes')
            ) : row.structuredOutputs === false ? (
              t('ai.catalog.structuredNo', "No (can't be assigned)")
            ) : (
              <>
                {t('ai.catalog.structuredNotChecked', 'Not checked')}
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('ai.catalog.structuredHint', 'Verify fills this in.')}
                </Typography>
              </>
            )}
          </TableCell>
          <TableCell>
            {/* One chip per tier the server lists in `usedBy` (FAST, DEEP), as sent. */}
            {row.usedBy.includes('FAST') || row.usedBy.includes('DEEP') ? (
              <Stack direction="row" spacing={0.5}>
                {row.usedBy.includes('FAST') ? (
                  <StatusChip tone="info" label={t('ai.models.fast', 'Fast tier')} />
                ) : null}
                {row.usedBy.includes('DEEP') ? (
                  <StatusChip tone="info" label={t('ai.models.deep', 'Deep tier')} />
                ) : null}
              </Stack>
            ) : (
              NOT_SENT
            )}
          </TableCell>
          <TableCell>
            {verifying === row.id ? (
              <Typography variant="body2" color="text.secondary">
                {t('ai.catalog.checking', 'Checking with Anthropic…')}
              </Typography>
            ) : typeof row.providerCheckedAt === 'string' && row.providerCheckedAt !== '' ? (
              formatInstant(row.providerCheckedAt, locale)
            ) : (
              t('ai.catalog.notVerified', 'Not verified')
            )}
          </TableCell>
          <TableCell>{row.note ?? NOT_SENT}</TableCell>
          <TableCell>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
            >
              <Button
                size="small"
                aria-label={t('ai.catalog.addPriceFor', 'Add price for {{model}}', {
                  model: row.modelId,
                })}
                onClick={() => setPriceFor(row.modelId)}
              >
                {t('ai.catalog.addPrice', 'Add price…')}
              </Button>
              <RowActionsMenu
                name={row.modelId}
                disabled={verifying !== null}
                items={[
                  {
                    key: 'addPrice',
                    label: t('ai.catalog.addPrice', 'Add price…'),
                    onSelect: () => setPriceFor(row.modelId),
                  },
                  {
                    key: 'edit',
                    label: t('ai.catalog.editTiers', 'Edit tiers…'),
                    onSelect: () => setEditing(row),
                  },
                  {
                    key: 'verify',
                    label: t('ai.catalog.verify', 'Verify'),
                    onSelect: () => void verify(row),
                  },
                  {
                    key: 'retire',
                    label: t('ai.catalog.retire.action', 'Retire…'),
                    onSelect: () => setRetiring(row),
                    destructive: true,
                  },
                ]}
              />
            </Stack>
          </TableCell>
        </TableRow>
        {open ? (
          <TableRow id={disclosureId}>
            <TableCell colSpan={COLUMNS} sx={{ bgcolor: 'action.hover' }}>
              {priceTable(
                row.modelId,
                prices.filter((price) => price.model === row.modelId),
              )}
            </TableCell>
          </TableRow>
        ) : null}
      </Fragment>
    );
  };

  const retiredRow = (row: AiModelCatalogRow) => (
    <TableRow key={row.id}>
      <TableCell>
        <Typography variant="body2" sx={(theme) => ({ ...theme.typography.mono, pl: 4.5 })}>
          {row.modelId}
        </Typography>
        <Box sx={{ pl: 4.5 }}>
          <LifecycleChip row={row} locale={locale} />
        </Box>
      </TableCell>
      <TableCell colSpan={COLUMNS - 1}>
        <Typography variant="body2">
          {t('ai.catalog.retiredLine', 'Retired {{date}} by {{who}}', {
            date: formatDate(row.retiredAt, locale),
            who: row.retiredBy ?? NOT_SENT,
          })}
        </Typography>
        {typeof row.retireNote === 'string' && row.retireNote !== '' ? (
          <Typography variant="body2" color="text.secondary">
            {row.retireNote}
          </Typography>
        ) : null}
      </TableCell>
    </TableRow>
  );

  const visibleRetired = showRetired ? retired : [];

  return (
    <Stack spacing={2}>
      {verifyRefusal ? <RefusalAlert refusal={verifyRefusal} /> : null}
      <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
        <AppSwitch
          label={t('ai.catalog.showRetired', 'Show retired')}
          checked={showRetired}
          onChange={onShowRetiredChange}
        />
      </Stack>
      {active.length === 0 && visibleRetired.length === 0 ? (
        <EmptyState
          surface
          title={t(
            'ai.catalog.empty',
            'No models yet. Add one to price it and assign it to a tier.',
          )}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('ai.catalog.tableAria', 'AI model catalogue')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.col.model', 'Model')}</TableCell>
                <TableCell>{t('ai.catalog.col.tiers', 'Tiers')}</TableCell>
                <TableCell>{t('ai.catalog.col.efforts', 'Efforts')}</TableCell>
                <TableCell>{t('ai.catalog.col.structured', 'Structured output')}</TableCell>
                <TableCell>{t('ai.catalog.col.usedBy', 'In use by')}</TableCell>
                <TableCell>{t('ai.catalog.col.verified', 'Verified')}</TableCell>
                <TableCell>{t('datedSettings.col.note', 'Note')}</TableCell>
                <TableCell align="right">{t('datedSettings.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {active.map(activeRow)}
              {visibleRetired.map(retiredRow)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {outside.length > 0 ? (
        <Box>
          <Button
            size="small"
            aria-expanded={outsideOpen}
            aria-controls={outsideOpen ? 'prices-outside-catalogue' : undefined}
            onClick={() => setOutsideOpen((open) => !open)}
          >
            {outsideOpen
              ? t('ai.catalog.hideOutside', 'Hide prices for models outside the catalogue')
              : t(
                  'ai.catalog.showOutside',
                  'Show {{count, number}} prices for models outside the catalogue',
                  {
                    count: outside.length,
                  },
                )}
          </Button>
          {outsideOpen ? (
            <Stack id="prices-outside-catalogue" spacing={2} sx={{ mt: 1 }}>
              {outsideRetired.length > 0 ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" component="h2">
                    {t('ai.catalog.retiredGroup', 'Retired models')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'ai.catalog.retiredCaption',
                      'This model is retired. Add it again on this tab to use it; its prices stay.',
                    )}
                  </Typography>
                  <ModelPricesTable
                    rows={outsideRetired}
                    loading={false}
                    locale={locale}
                    ariaLabel={t('ai.catalog.retiredAria', 'Prices for retired models')}
                    emptyTitle={t('ai.catalog.retiredAria', 'Prices for retired models')}
                    historyModel={historyModel}
                    onToggleHistory={onToggleHistory}
                    onReload={reread}
                  />
                </Stack>
              ) : null}
              {outsideUnknown.length > 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'ai.catalog.outsideCaption',
                      "These can't be assigned. Add the model to the catalogue to use it.",
                    )}
                  </Typography>
                  <ModelPricesTable
                    rows={outsideUnknown}
                    loading={false}
                    locale={locale}
                    ariaLabel={t(
                      'ai.catalog.outsideAria',
                      'Prices for models outside the catalogue',
                    )}
                    emptyTitle={t(
                      'ai.catalog.outsideAria',
                      'Prices for models outside the catalogue',
                    )}
                    historyModel={historyModel}
                    onToggleHistory={onToggleHistory}
                    onReload={reread}
                  />
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      <EditTiersDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          notify.success(t('ai.catalog.edit.saved', 'Tiers saved.'));
          reread();
        }}
        onReread={reread}
      />
      <RetireModelDialog
        row={retiring}
        onClose={() => setRetiring(null)}
        onRetired={(row) => {
          setRetiring(null);
          notify.success(t('ai.catalog.retired', '{{model}} retired.', { model: row.modelId }));
          reread();
        }}
        onReread={reread}
      />
      <AddPriceDialog
        open={priceFor !== null || change !== null}
        model={priceFor ?? ''}
        change={change}
        onClose={() => {
          setPriceFor(null);
          setChange(null);
        }}
        onAdded={() => {
          const changed = change !== null;
          setPriceFor(null);
          setChange(null);
          notify.success(
            changed ? t('ai.changed', 'Price changed.') : t('ai.added', 'Price added.'),
          );
          reread();
        }}
      />
    </Stack>
  );
}
